import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from './messages'
import Icon from 'components/Icon'
import Expandable from '../Expandable'
import { clientId } from 'utils'
import Rate from './components/Rate'
import TimestampWidget from 'rest/views/coordinator/supplier/data/timestamp/MainView'
import { Button, SelectInline } from '@arnsbogroup/react-voicearchive'
import useApi from 'hooks/useApi'
import useCurrentUser from 'hooks/useCurrentUser'

const useCollection = (collection, callback) => {
  fetch(`/rest/v2/${collection}?pageSize=1000`, { credentials: 'same-origin' }) 
    .then((response) => response.json())
    .then((next) => callback((prev) => ({
      ...prev,
      ...next.reduce((memo, item) => {
        memo[item.uid] = item
        return memo
      }, {})
    })))
    .catch((error) => {
      window.alert(error)
    })
}

const groupByProductId = (rates) => (
  Object.values(rates).reduce((memo, rate) => {
    if (rate.product) {
      (memo[rate.product.id] = memo[rate.product.id] || []).push(rate)
    } else {
      (memo[0] = memo[0] || []).push(rate)
    }
    return memo
  }, {})
)

const useRates = (standardRates, customRates, optional, customize = false) => {
  const nextRates = {}

  Object.entries(standardRates)
    .forEach(([id, standardRate]) => {
      if (optional.includes(standardRate.id) || customize) {
        const customRate = Object.values(customRates)
          .find(({ parent_id }) => parent_id === standardRate.id)

        if (customRate) {
          nextRates[customRate.id] = customRate
        } else {
          const duplicateRate = {
            ...standardRate,
            id: clientId(),
            parent_id: standardRate.id,
          }
          nextRates[duplicateRate.id] = duplicateRate
        }
      } else {
        nextRates[id] = standardRate
      }
    })

  return Object.values(nextRates)
}

const Rates = ({
  parentRef = null,
  setCallback = () => {},
  match: { params: { supplierId } },
  readOnly = false,
  embedded = false,
}) => {
  const { roles } = useCurrentUser()
  const isCoordinator = roles.includes('coordinator')
  const { formatMessage } = useIntl()
  const [medias, setMedias] = useState({})
  const [services, setServices] = useState({})
  const [expanded, setExpanded] = useState({})
  const [isInternalSupplier, setIsInternalSupplier] = useState(false)
  const [products, setProducts] = useState({
    0: { name: 'Extra' },
  })

  const [showCustom, setShowCustom] = useState(false)
  const [rates, setRates] = useState([])
  const [state, setState] = useState({
    standard: [],
    custom: [],
    optional: [],
    accepted: []
  })

  const [currencies] = useApi(isCoordinator ? `/rest/v1/currency?search[hidden]=0` : null)
  const [supplier, setSupplier] = useState(null)

  const currencyOptions = useMemo(() => (
    (currencies || []).map(({ uid, iso }) => ({ value: uid, label: iso }))
  ), [currencies])

  // The read endpoint defaults an unset currency to EUR, so only the raw
  // `currencyId` says whether one was ever picked - showing the default as if it
  // were stored also makes picking EUR look like a no-op and skip the save
  const selectedCurrency = useMemo(() => (
    supplier?.currencyId
      ? currencyOptions.find(({ value }) => value == supplier.currencyId) || null
      : null
  ), [currencyOptions, supplier])

  useEffect(() => useCollection('ordertargetmedia', setMedias), [])
  useEffect(() => useCollection('order_services', setServices), [])
  useEffect(() => useCollection('product', setProducts), [])

  useEffect(() => {
    fetch(`/rest/v2/supplier/${supplierId}/rates`, { credentials: 'same-origin' }) 
      .then((response) => response.json())
      .then(({ data, meta }) => {
        setShowCustom(meta.custom && !meta.standard)

        setState({
          ...data,
          optional: meta.optional,
          accepted: meta.accepted,
        })
      })
      .catch((error) => window.alert(error))

      fetch(`/rest/v2/supplier/${supplierId}?includeRelations=1`, { credentials: 'same-origin' }) 
        .then((response) => response.json())
        .then((response) => {
          setSupplier(response)
        })
        .catch((error) => window.alert(error))
      
  }, [supplierId])

  useEffect(() => {
    setCallback((event) => new Promise((resolve, reject) => {
      if (!showCustom && rates.some(({ id }) => (
        !state.optional.includes(id) &&
        !state.accepted.includes(id) &&
        !id.toString().startsWith('#')
      ))) {
        event.stopPropagation()
        event.preventDefault()

        return reject(formatMessage(messages.validationFailed))
      }

      return
    }))
  }, [showCustom, rates, state])
  
  useEffect(() => {
    if (supplier && !!supplier.priorities.length) {
      if (supplier.priorities.some((priority) => priority.slug == 'internal_supplier')) {
        setIsInternalSupplier(true)
        setShowCustom(true)
      }
    }
  }, [supplier, showCustom])

  useEffect(() => {
    const { standard, custom, optional } = state
    setRates(useRates(standard, custom, optional, showCustom))
  }, [showCustom, state])

  const updateSupplierCurrency = (currencyId) => {
    const currency = (currencies || []).find(({ uid }) => uid == currencyId)

    if (!currency || currency.uid == supplier?.currencyId) {
      return
    }

    const previousSupplier = supplier
    setSupplier({ ...supplier, currency, currencyId: currency.uid })

    fetch(`/rest/v2/supplier/${supplierId}`, {
      credentials: 'same-origin',
      method: 'PUT',
      body: JSON.stringify({ currency: currency.uid }),
    })
    .then((response) => {
      if (!response.ok) {
        throw new Error(response.statusText)
      }

      toastr.success(formatMessage(messages.currencyUpdateSuccess))
    })
    .catch((error) => {
        setSupplier(previousSupplier)
        toastr.error(error.message || error)
    })
  }
  
  const onAccept = (rateIds) => {
    fetch(`/rest/v2/supplier/${supplierId}/rates/accept`, {
      credentials: 'same-origin',
      method: 'POST',
      body: JSON.stringify(rateIds),
    })
      .then((response) => response.json())
      .then(({ data: { accepted } }) => setState({ ...state, accepted }))
      .catch((error) => window.alert(error))
  }
  
  const onChange = useCallback((rateId, nextRate) => {
    const nextState = { ...state }
    delete nextState.custom[rateId]
    nextState.custom[nextRate.id] = nextRate
    nextState.accepted = [...nextState.accepted, nextRate.id]

    setState(nextState)
  }, [rates])

  const groupedAndSortedEntries = useMemo(() => {
    return Object.entries(groupByProductId(rates))
      .sort(([a, _a], [b, _b]) => {
        return b > 0 ? (a > b ? 1 : -1) : -1
      })
      .sort((a, [_, rates]) => {
        return rates.some(({ id, parent_id }) => (
          state.optional.includes(id) ||
          state.optional.includes(parent_id)
        )) ? -1 : 1
      })
  }, [rates, state])

  return (
    
    <div className={`${isCoordinator ? 'grid grid-cols-2 gap-4' : ''}`}>
      <div className="w-full pb-40 ">
      { !embedded &&
        <React.Fragment>
          <div className="font-medium text-4xl">
            <FormattedMessage {...messages.title } />
          </div>

          <div className="text-gray-700 mt-6 w-2/3">
            {!isInternalSupplier ? (
              <FormattedMessage {...messages.subtitle} values={{
                bold: (chunk) => <b className="text-medium">{ chunk }</b>
              }} />
            ) : (
              <FormattedMessage {...messages.internalSupplierSubtitle} values={{
                bold: (chunk) => <b className="text-medium">{ chunk }</b>
              }} />
            )}
            
          </div>
          {!isInternalSupplier &&
            <Expandable
              className="mt-4 text-base"
              expandLabel={formatMessage(messages.explanationExpandLabel)}
              collapseLabel={formatMessage(messages.explanationCollapseLabel)}
            >
              <p className="text-base">
                <FormattedMessage {...messages.explanationContent} />
              </p>
            </Expandable>
          }

          <div className="border border-gray-400 p-4 text-sm my-10 text-gray-800 bg-yellow-200 whitespace-pre-line">
            <FormattedMessage {...messages.effectNotice} values={{
              bold: (chunk) => <b className="text-medium">{ chunk }</b>
            }} />
          </div>

          {!isInternalSupplier && supplier &&
            <div className="flex justify-between items-end mt-10">
              <div className="block w-1/12 border-b border-gray-400" />
                <div className={`w-5/12 px-6 py-4 border-gray-400 cursor-pointer ${showCustom ? 'border-b' : 'border-l border-t border-r text-primary'}`} onClick={() => setShowCustom(false)}>
                  <div>
                    <FormattedMessage {...messages.standardLabel}  />
                  </div>
                  <div className="text-sm text-gray-500 hidden md:block">
                    <FormattedMessage {...messages.standardLabelHint}  />
                  </div>
                </div>
              <div
              className={`w-5/12 px-6 py-4 border-gray-400 ${!showCustom ? 'border-b cursor-not-allowed hover:text-red-500 hover:border' : 'border-l border-t border-r text-red-700'}`}
              onClick={() => window.alert(formatMessage(messages.customisedLabelWarning))}
              >
                <div>
                  <FormattedMessage {...messages.customisedLabel}  />
                </div>
                <div className="text-sm text-gray-500 hidden md:block">
                  <FormattedMessage {...messages.customisedLabelHint}  />
                </div>
              </div>
              <div className="block w-1/12 border-b border-gray-400" />
            </div>
          }

          { showCustom &&
              <div className="border border-gray-400 bg-red-100 px-6 py-4 mt-8 text-sm text-red-900">
                <FormattedMessage {...messages.customisedWarning}  />
              </div>
          }
        </React.Fragment>
      }

      { !groupedAndSortedEntries.length || !supplier && [1, 2, 3, 4].map((i) => (
        <div key={i} className="block h-20 border mt-8 border-gray-400 bg-gray-100 cursor-wait animate-pulse" />
      )) }

      { groupedAndSortedEntries.map(([productId, rates]) => {
        const optional = rates.some(({ id }) => id.toString().startsWith('#'))

        return (
          <div
            tabIndex={0}
            key={productId}
            className={`border mt-8 ${optional && !expanded[productId] ? 'border-gray-300 border-dashed text-gray-600 cursor-pointer hover:border-gray-400 hover:text-black' : 'border-gray-400'}`}
          >
            <div
              className="p-6 flex justify-between items-center"
              onClick={() => {
                if (optional && !expanded[productId]) {
                  setExpanded((prev) => ({ ...prev, [productId]: prev[productId] ? false : true }) )
                }
              }}
            >
              <div className="flex justify-start items-center">
                <Icon
                  className="text-xl mr-3"
                  icon={{
                    0: ['fal', 'layer-plus'],
                    37: ['fal', 'gamepad-alt'],
                    40: ['fal', 'users-class'],
                    52: ['fal', 'podcast'],
                    10: ['fal', 'microchip'],
                    2: ['fal', 'volume-up'],
                    1: ['fal', 'book'],
                    11: ['fal', 'tv-retro'],
                    12: ['fal', 'megaphone'],
                    3: ['fal', 'graduation-cap'],
                    46: ['fal', 'voicemail'],
                    5: ['fal', 'megaphone'],
                    55: ['fal', 'user-astronaut'],
                  }[productId]}
                />

                <div className="text-lg">
                  { productId in products ? products[productId].name : productId }

                  <Icon
                    title={formatMessage(messages[`productHint${productId}`])}
                    icon={['fal', 'question-circle']}
                    className="text-sm cursor-pointer ml-1 hover:text-primary"
                    onClick={() => window.alert(formatMessage(messages[`productHint${productId}`]))}
                  />
                </div>
              </div>
              <div className="space-x-3">
                { readOnly && rates.every(({ id }) => state.accepted.includes(id)) ? (
                  <React.Fragment>
                    { Object.values(state.custom).some(({id}) => state.accepted.includes(id)) ? (
                      <span className="text-sm"><FormattedMessage {...messages.acceptedProductCustom } /></span>
                    ) : (
                      <span className="text-sm"><FormattedMessage {...messages.acceptedProduct } /></span>
                    )}
                    <span className="w-2 h-2 rounded-full inline-block bg-green-400" />
                  </React.Fragment>
                ) : readOnly && rates.some(({ id }) => state.accepted.includes(id)) ? (
                  <React.Fragment>
                    <span className="text-sm"><FormattedMessage {...messages.partiallyAcceptedProduct } /></span>
                    <span className="w-2 h-2 rounded-full inline-block bg-purple-400" />
                  </React.Fragment>
                ) : optional ? (
                  <React.Fragment>
                    <span className="text-sm"><FormattedMessage {...messages.optionalProduct } /></span>
                    <span className="w-2 h-2 rounded-full inline-block bg-yellow-400" />
                  </React.Fragment>
                ) : (
                  !showCustom && rates.some(({ id }) => !state.accepted.includes(id)) ? (
                    <React.Fragment>
                      <span className="text-sm">
                        <FormattedMessage {...messages.requiredProduct} />
                      </span>
                      <span className="w-2 h-2 rounded-full inline-block bg-purple-400" />
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      <span className="text-sm">
                        <FormattedMessage {...messages.acceptedProduct} />
                      </span>
                      <span className="w-2 h-2 rounded-full inline-block bg-green-400" />
                    </React.Fragment>
                  )
                ) }

                <Icon
                  icon={['fas', expanded[productId] ? 'chevron-up' : optional ? 'plus' : 'chevron-down']}
                  className="text-sm cursor-pointer text-gray-400 hover:text-gray-700"
                  onClick={(event) => {
                    event.stopPropagation()
                    setExpanded((prev) => ({ ...prev, [productId]: prev[productId] ? false : true }) )
                  }}
                />
              </div>
            </div>

            { expanded[productId] && rates.map((rate) => (
              <Rate
                accepted={state.accepted.some((id) => id == rate.id)}
                custom={!!state.custom[rate.id]}
                key={rate.id}
                rate={rate}
                supplierId={supplierId}
                onChange={onChange}
                readOnly={readOnly || (!showCustom && !state.optional.includes(rate.parent_id))}
              />
            )) }
          </div>
        )
      }) }

      { !embedded && !isInternalSupplier &&
        <React.Fragment>
          { !showCustom && rates.some(({ id }) => (
            !state.optional.includes(id) &&
            !state.accepted.includes(id) &&
            !id.toString().startsWith('#')
          )) &&
          <div className="mt-8 flex w-full space-x-4">
            <div className="w-1/2">
              <Button
                variant="outline"
                state="negative"
                size="sm"
                className="w-full"
                onClick={() => {
                  if (parentRef && parentRef.current) {
                    parentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                  setShowCustom(true)
                }}
              >
                <FormattedMessage {...messages.declineButtonLabel } />
              </Button>
              <div className="text-sm mt-2 text-center text-gray-600">
                <Icon icon={['far', 'info-circle']} className="mr-2 text-gray-600" />
                <FormattedMessage {...messages.declineButtonSubLabel } />
              </div>
            </div>

            <div className="w-1/2">
              <Button
                variant="outline"
                state="positive"
                size="sm"
                className="w-full"
                onClick={() => onAccept(
                  rates.filter(({ id }) =>
                    !state.optional.includes(id) &&
                    !state.accepted.includes(id) &&
                    !id.toString().startsWith('#')
                  ).map(({ id }) => id)
                )}
              >
                <FormattedMessage {...messages.acceptButtonLabel } />
              </Button>
              <div className="text-sm"></div>
            </div>
          </div>
          }
        </React.Fragment>
      }
      </div>
      {isCoordinator && (
        <div className="w-full pb-40 space-y-2 mt-7">
          { !supplier || !currencies ? (
            <div className="block h-10 w-40 border border-gray-400 bg-gray-100 cursor-wait animate-pulse" />
          ) : (
            <SelectInline
              label={formatMessage(messages.currencySelectLabel)}
              value={selectedCurrency}
              options={currencyOptions}
              placeholder="ISO"
              onChange={(option) => updateSupplierCurrency(option?.value)}
            />
          )}
          <TimestampWidget supplier={{uid: supplierId}} strategy="rate-changes" />
        </div>
      )}
      
    </div>
  )
}

const RatesList = (props) => (
  <Rates {...props} readOnly embedded />
)

export {
  Rates as default,
  Rates,
  RatesList
}
